<template>
  <b-container>
    <div v-if="sysAdmin || canUseForms">
      <b-tabs
          vertical
          nav-wrapper-class="col-3"
          nav-class="mt-5"
          v-model="tabIndex"
          @input="scrollToTop"
          lazy
      >
        <template #tabs-start>
          <b-button
              v-b-modal.modal-add-new-form-item class="green-button-transparent mr-3 mb-3 border-0"
          >
            <b-icon
                icon="plus-circle-fill"
                class="mr-2 top--3"
                aria-hidden="true"
                scale="1"
            ></b-icon>
            {{ $t('formManagement.createFormItem') }}
          </b-button>
          <modal-add-new-form-item
              class="d-inline-block mb-3"
              @create-form-item="createFormItem"
          ></modal-add-new-form-item>
        </template>
        <template #empty>
          <div class="mt-5" v-if="allowedToCreateForms">
            <p class="text-center" v-html="$t('formManagement.howToFormDesc')"></p>
          </div>
          <div class="mt-5" v-else>
            {{ $t('formManagement.emptyForm') }}
          </div>
        </template>
        <b-tab v-for="(formItem, index) in formItems" :key="index" lazy>
          <template v-slot:title>
            {{ formItem.name }}
          </template>
          <form-item-list-view
              class="mt-5"
              :userId="userId"
              :formItem="formItem"
              :all-forms="formItems"
              :formItemId="formItem.form_id"
              :formIndex="index"
              :isSysAdmin="sysAdmin"
              :group-id="formsGroupId"
              @changeInFormList="updateFormItemQuery"
          ></form-item-list-view>
        </b-tab>
      </b-tabs>
    </div>
    <div v-else-if="user !== null" class="pt-5">
      <p class="text-center">{{ $t('adminAccountManagement.notAllowed') }}</p>
    </div>
  </b-container>
</template>

<script>
import CreateForm from '@/graphQlQueries/mutations/forms/createForm';
import UpdateForm from '@/graphQlQueries/mutations/forms/updateForm';
// import SetLoadingIndicator from '@/graphQlQueries/mutations/setLoadingIndicatorInCache';
import SetShowHeaderInCache from '@/graphQlQueries/mutations/setShowHeaderInCache';
import SetPageTypeInCache from '@/graphQlQueries/mutations/setPageTypeInCache';
import SetRunBreadcrumbQueryInCache from '@/graphQlQueries/mutations/setRunBreadcrumbQueryInCache';
import UserAtLeastManagerInOneGroup from '@/graphQlQueries/queries/accessCheckForManagementAreas';
import GetForms from '@/graphQlQueries/queries/forms/getForms';
import GetUserByEmail from '@/graphQlQueries/queries/getUserByEmail';
import GetLastGroupId from '@/graphQlQueries/queries/getLastGroupId';
import GetGroupDataByGroupId from '@/graphQlQueries/queries/getGroupDataByGroupId';
import isGroupForbiddenError from '@/views/formCreateGroupRetry';

import Auth from '@aws-amplify/auth';

export default {
  name: 'Form',
  components: {
    FormItemListView: () => import('@/components/settings/forms/FormItemListView.vue'),
    ModalAddNewFormItem: () => import('@/components/modals/forms/ModalAddNewFormItem.vue'),
  },
  data: () => ({
    pageType: 'form-settings',
    allowedToCreateForms: false,
    isSysAdmin: null,
    canUseForms: false,
    newOrChangedFormId: null,
    showAddNewFormItemModal: false,
    showEditTeamModal: false,
    userId: 1,
    user: null,
    pageId: [],
    formItems: [],
    number: 342,
    tabIndex: 0,
    hashDeepLinkApplied: false,
    formsGroupId: null,
  }),
  async created() {
    const currentSession = await Auth.currentSession();
    const idTokenPayload = JSON.parse(currentSession.getIdToken().payload['https://hasura.io/jwt/claims']);
    this.isSysAdmin = idTokenPayload['x-hasura-allowed-roles'].includes('admin');
    this.userId = (idTokenPayload['x-hasura-user-id'])
      ? Math.floor(idTokenPayload['x-hasura-user-id'])
      : null;
    if (this.userId) {
      // check if the user is admin
      if (idTokenPayload['x-hasura-allowed-roles'].includes('admin')) {
        this.$currentUserRole = 'admin';
      } else {
        this.$currentUserRole = 'user';
      }
    }
    this.userEmail = Auth.user.attributes.email;
    const {
      data: {
        users: [user],
      },
    } = await this.$apollo.query({
      query: GetUserByEmail,
      variables: {
        email: this.userEmail,
      },
    });
    this.user = {
      userId: this.userId,
      name: user.full_name || null,
      email: Auth.user.attributes.email,
      isSysAdmin: user.sys_admin,
    };
    // Determine canUseForms from the group privilege
    try {
      const groupIdResult = await this.$apollo.query({
        query: GetLastGroupId,
        variables: { email: this.userEmail },
      });
      const groupUserObj = groupIdResult.data.users[0];
      const groupId = groupUserObj
        ? (groupUserObj.last_group_id ?? groupUserObj.home_group_id)
        : null;
      this.formsGroupId = groupId;
      if (groupId) {
        const groupData = await this.$apollo.query({
          query: GetGroupDataByGroupId,
          variables: { groupId },
        });
        const privObj = groupData.data.groups_by_pk
          && groupData.data.groups_by_pk.groups_privileges.find(
            (gp) => gp.privilege.handle === 'can_use_forms',
          );
        if (privObj) {
          this.canUseForms = privObj.value === true || privObj.value === 'true';
        } else {
          // fall back to the default_value from the privileges catalog
          const allPrivs = groupData.data.privileges || [];
          const defaultPriv = allPrivs.find((p) => p.handle === 'can_use_forms');
          this.canUseForms = defaultPriv
            ? (defaultPriv.default_value === true || defaultPriv.default_value === 'true')
            : false;
        }
      }
    } catch (err) {
      console.error('Form: failed to load can_use_forms privilege', err);
    }
    await this.$apollo.mutate({
      mutation: SetPageTypeInCache,
      variables: {
        type: this.pageType,
      },
    });
    await this.$apollo.mutate({
      mutation: SetRunBreadcrumbQueryInCache,
      variables: {
        runQuery: true,
      },
    });
    // this mutation is used to display the header of the application
    await this.$apollo.mutate({
      mutation: SetShowHeaderInCache,
      variables: {
        show: true,
      },
    });
  },
  computed: {
    sysAdmin() {
      return this.isSysAdmin;
    },
  },
  watch: {
    tabIndex(newVal) {
      const form = this.formItems[newVal];
      if (form) {
        const newHash = `#form-${form.form_id}`;
        if (window.location.hash !== newHash) {
          window.history.replaceState(null, '', newHash);
        }
      }
    },
  },
  apollo: {
    allowedToCreateForms: {
      query: UserAtLeastManagerInOneGroup,
      update(data) {
        return data.checkAccessToManagementAreas;
      },
    },
    formItems: {
      query: GetForms,
      update(data) {
        const formObjects = [];
        if (data.getForms) {
          data.getForms.forEach((formItem) => {
            const element = formItem;
            formObjects.push(element);
          });
        }

        formObjects.sort((a, b) => {
          const textA = a.name.toUpperCase();
          const textB = b.name.toUpperCase();
          // eslint-disable-next-line no-nested-ternary
          return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
        });

        if (this.newOrChangedFormId === null && !this.hashDeepLinkApplied) {
          const hashMatch = window.location.hash.match(/^#form-(\d+)$/);
          if (hashMatch) {
            const formId = parseInt(hashMatch[1], 10);
            const hashIndex = formObjects.findIndex((f) => f.form_id === formId);
            if (hashIndex !== -1) {
              this.hashDeepLinkApplied = true;
              this.$nextTick(() => {
                this.tabIndex = hashIndex;
              });
            }
          }
        }

        if (this.newOrChangedFormId !== null) {
          const newOrChangedFormIndex = formObjects.findIndex(
            (form) => form.form_id === this.newOrChangedFormId,
          );
          if (newOrChangedFormIndex !== -1) {
            // this is a workaround to update the active tab on create a new team
            // to respect the update cycle of the b-tabs
            this.$nextTick(() => {
              this.$nextTick(() => {
                this.$nextTick(() => {
                  this.$nextTick(() => {
                    requestAnimationFrame(() => {
                      this.tabIndex = newOrChangedFormIndex;
                      this.newOrChangedFormId = null;
                    });
                  });
                });
              });
            });
          }
        }
        return formObjects;
      },
      skip() {
        return !this.userId;
      },
      fetchPolicy: 'network-only',
    },
  },
  methods: {
    scrollToTop() {
      window.scrollTo(0, 0);
    },
    async createFormItem(formObject) {
      try {
        const newFormId = await this.insertNewForm(formObject);
        console.log('new form id', newFormId);
        this.newOrChangedFormId = newFormId;
        this.number += 1;
        this.$apollo.queries.formItems.refresh();
      } catch (ex) {
        console.error(ex);
      }
    },
    updateForm(formId, templateFormData) {
      return new Promise((resolve, reject) => {
        this.$apollo.mutate({
          mutation: UpdateForm,
          variables: {
            obj: {
              form_id: formId,
              form_data: templateFormData,
            },
          },
        }).then((data) => {
          console.log('data update form', data);
          resolve(data);
        }).catch((ex) => {
          console.error(ex);
          reject(ex);
        });
      });
    },
    async insertNewForm(formObject) {
      const baseObj = {
        name: formObject.name,
        description: formObject.desc,
        owner_id: this.userId,
      };
      try {
        const { data: { createForm: { newFormId } } } = await this.$apollo.mutate({
          mutation: CreateForm,
          variables: { obj: { ...baseObj, group_id: this.formsGroupId || undefined } },
        });
        return newFormId;
      } catch (ex) {
        if (this.formsGroupId && isGroupForbiddenError(ex)) {
          const { data: { createForm: { newFormId } } } = await this.$apollo.mutate({
            mutation: CreateForm,
            variables: { obj: baseObj },
          });
          return newFormId;
        }
        console.error(ex);
        throw ex;
      }
    },
    async updateFormItemQuery(formItemId) {
      if (formItemId === -1) {
        this.tabIndex = 0;
      } else {
        this.newOrChangedFormId = formItemId;
      }
      this.$apollo.queries.formItems.refresh();
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep  .btn.dot-button {
  color: #b9b9b9;
  background: transparent;
  border: none;
  padding: 5px;

  &:hover {
    color: #fff;
  }
}
</style>
